import type { RhythmStepKind } from "../../lib/rhythm/rhythmPathPlan";
import { RHYTHM_STEP_KIND_LABEL } from "../../lib/rhythm/rhythmPathPlan";

export interface SubMissionStepperProps {
  current: number;
  total: number;
  /** Títulos cortos opcionales por paso. */
  labels?: string[];
  /** Tipo pedagógico del paso actual (badge Resistencia / Examen). */
  kind?: RhythmStepKind | null;
  className?: string;
}

/**
 * Contador circular de progreso interno de una misión de ritmo.
 */
export function SubMissionStepper({
  current,
  total,
  labels,
  kind = null,
  className = "",
}: SubMissionStepperProps) {
  if (total <= 1) return null;

  const showBadge = kind === "resistencia" || kind === "examen";
  const done = Math.min(total, Math.max(0, current));
  const progress = Math.min(1, Math.max(0, (current + 1) / total));
  const size = 44;
  const stroke = 3.5;
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ * (1 - progress);

  return (
    <div
      className={`flex flex-col items-end gap-1 ${className}`}
      role="status"
      aria-label={`Paso ${current + 1} de ${total}${
        showBadge && kind ? `, ${RHYTHM_STEP_KIND_LABEL[kind]}` : ""
      }`}
      title={labels?.[current] ?? `Paso ${current + 1}`}
    >
      {showBadge && kind && (
        <span
          className={`rounded-md px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-[0.12em] ${
            kind === "examen"
              ? "bg-amber-400/20 text-amber-200/95 ring-1 ring-amber-300/35"
              : "bg-[#99f6e4]/15 text-[#99f6e4] ring-1 ring-[#99f6e4]/35"
          }`}
        >
          {RHYTHM_STEP_KIND_LABEL[kind]}
        </span>
      )}
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="-rotate-90" aria-hidden>
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke="rgba(255,255,255,0.12)"
            strokeWidth={stroke}
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke={
              kind === "examen"
                ? "rgba(251, 191, 36, 0.9)"
                : "#99f6e4"
            }
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={circ}
            strokeDashoffset={offset}
            className="transition-[stroke-dashoffset] duration-300"
          />
        </svg>
        <span className="absolute inset-0 flex items-center justify-center text-[11px] font-semibold tabular-nums text-white/85">
          {current + 1}
          <span className="text-white/40">/{total}</span>
        </span>
      </div>
      <span className="sr-only">
        {done} completados de {total}
      </span>
    </div>
  );
}
